/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import models.BudgetCategoryGroup
import models.BudgetCategoryProvider
import models.BudgetCustomCategory
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.get
import org.w3c.fetch.RequestInit
import kotlin.js.Date
import kotlin.js.json as jsJson
import kotlin.math.round

/**
 * Budget Planning spreadsheet-style component for annual budget planning.
 * Uses server API for cross-platform sync.
 */
class WebBudgetPlanningComponent(
    private val navigationManager: WebNavigationManager,
    private val onAuthError: () -> Unit = {}
) {
    private var selectedYear = Date().getFullYear()
    private var container: HTMLElement? = null
    private val apiService = ApiService()
    private var isLoading = false

    // Budget data structure: Map<category code, Map<month, amount>>
    private val incomeData = mutableMapOf<String, MutableMap<Int, Double>>()
    private val expenseData = mutableMapOf<String, MutableMap<Int, Double>>()

    // Category hierarchy from BudgetCategoryProvider
    private var categoryGroups = listOf<BudgetCategoryGroup>()
    private val expandedGroups = mutableSetOf<String>()
    private var customCategories = listOf<CustomCategoryData>()

    private val monthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

    /** Format a cell value for display: show decimals only if not a whole number */
    private fun formatCellValue(value: Double): String =
        if (value == 0.0) "" else if (value == kotlin.math.floor(value)) value.toInt().toString() else value.asDynamic().toFixed(2) as String

    /** Format a total for display: round to nearest integer */
    private fun formatTotal(value: Double): String =
        if (value == 0.0) "-" else "$${kotlin.math.round(value).toInt()}"

    /** Parse input value: accept both dot and comma as decimal separator */
    private fun parseInputValue(text: String): Double =
        text.replace(",", ".").toDoubleOrNull() ?: 0.0

    // Categories will be loaded dynamically from BudgetCategoryProvider in loadFromServer()

    fun render(): HTMLElement {
        val budgetContainer = document.createElement("div") as HTMLElement
        budgetContainer.className = "budget-planning-container"
        container = budgetContainer
        val t = WebTheme.current

        val isMobile = ResponsiveUtils.isMobile()
        val padding = if (isMobile) "16px" else "24px 32px"

        budgetContainer.innerHTML = """
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: transparent; min-height: 100vh;">
                <!-- Top Navigation Bar -->
                ${renderTopNav(isMobile)}

                <!-- Mobile Menu Overlay (hidden by default) -->
                ${if (isMobile) MarketingMenu.mobileOverlayHtml() else ""}

                <!-- Main Content (left/top padding only — table extends to right/bottom edges) -->
                <div style="padding: ${if (isMobile) "16px 0 0 16px" else "24px 0 0 32px"};">
                    <!-- Header with Year Selector -->
                    <div style="display: flex; ${if (isMobile) "flex-direction: column; gap: 12px;" else "justify-content: space-between; align-items: center;"} margin-bottom: 16px; padding-right: ${if (isMobile) "16px" else "32px"};">
                        <h1 style="color: ${t.textPrimary}; margin: 0; font-size: ${if (isMobile) "22px" else "28px"}; font-weight: 600;">${StringKey.BUDGET_TITLE.tr()}</h1>
                        <div style="display: flex; gap: ${if (isMobile) "8px" else "12px"}; align-items: center; ${if (isMobile) "flex-wrap: wrap;" else ""}">
                            <select id="yearSelector" data-guide="year-selector" style="
                                padding: 8px ${if (isMobile) "12px" else "16px"};
                                border: 1px solid ${t.border};
                                border-radius: 6px;
                                background: ${t.card};
                                color: ${t.textPrimary};
                                cursor: pointer;
                                font-size: ${if (isMobile) "13px" else "14px"};
                            ">
                                ${renderYearOptions()}
                            </select>
                            <button id="copyPrevYear" data-guide="copy-prev-year" style="
                                padding: 8px ${if (isMobile) "12px" else "16px"};
                                border: 1px solid ${t.border};
                                border-radius: 6px;
                                background: ${t.card};
                                color: ${t.accent};
                                cursor: pointer;
                                font-size: ${if (isMobile) "12px" else "14px"};
                            ">${StringKey.BUDGET_COPY_FROM_YEAR.tr((selectedYear - 1).toString())}</button>
                            <button id="exportBtn" style="
                                padding: 8px ${if (isMobile) "12px" else "16px"};
                                border: 1px solid ${t.border};
                                border-radius: 6px;
                                background: ${t.card};
                                color: ${t.accent};
                                cursor: pointer;
                                font-size: ${if (isMobile) "12px" else "14px"};
                            ">${StringKey.BUDGET_EXPORT_CSV.tr()}</button>
                        </div>
                    </div>

                    <!-- Budget Spreadsheet -->
                    <div id="budgetSpreadsheet" style="
                        background: ${t.card};
                        border-radius: 12px 0 0 0;
                        border: 1px solid ${t.border};
                        border-right: none;
                        border-bottom: none;
                        position: relative;
                        overflow: auto;
                        height: calc(100vh - 150px);
                    ">
                        ${renderSpreadsheet()}
                    </div>
                </div>
            </div>
        """

        // Setup event listeners after render
        setupEventListeners(budgetContainer)

        // Setup mobile menu listeners if on mobile
        if (isMobile) {
            setupMobileMenuListeners(budgetContainer)
        }

        // Load data from server after component is fully rendered
        loadFromServer()

        return budgetContainer
    }

    private fun renderTopNav(isMobile: Boolean = false): String {
        val t = WebTheme.current
        val logoSrc = if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"
        val tourHaloClass = if (!GuideOverlay.hasSeen(guide.GuideScreen.BUDGET)) "wiz3r-guide-halo" else ""
        val tourTooltip = StringKey.TOUR_OPEN_TOOLTIP.tr()
        return if (isMobile) {
            """
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: sticky; top: 0; z-index: 100;">
                <img id="budget-logo" src="$logoSrc" alt="wiz≡r" style="height: 32px; cursor: pointer;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                <div style="display: flex; align-items: center; gap: 8px;">
                    <span id="nav-tour" class="$tourHaloClass" style="width: 26px; height: 26px; border-radius: 50%; background: ${t.navBackground}; color: ${t.accent}; border: 1px solid ${t.border}; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; cursor: pointer; font-family: serif; user-select: none;" title="$tourTooltip">i</span>
                    <span id="nav-language" style="padding: 4px 8px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border};" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    <button id="hamburgerMenu" style="background: none; border: none; color: ${t.textPrimary}; font-size: 24px; cursor: pointer; padding: 4px;">☰</button>
                </div>
            </div>
            """
        } else {
            """
            <div style="display: flex; align-items: center; padding: 16px 32px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: fixed; top: 0; left: 0; right: 0; z-index: 100; transition: padding 0.25s ease;">
                <img id="budget-logo" src="$logoSrc" alt="wiz≡r" style="height: 56px; cursor: pointer; margin-right: 40px; transition: height 0.25s ease;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                <div style="display: flex; gap: 8px; flex: 1;">
                    <span id="nav-activity" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_TRANSACTIONS.tr()}</span>
                    <span id="nav-dashboard" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_DASHBOARD.tr()}</span>
                    <span id="nav-goals" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_GOALS.tr()}</span>
                    <span id="nav-budget" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: ${t.navBackground}; color: ${t.textPrimary};">${StringKey.NAV_BUDGET.tr()}</span>
                </div>
                <div style="display: flex; align-items: center; gap: 12px;">
                    <span id="nav-tour" class="$tourHaloClass" style="width: 28px; height: 28px; border-radius: 50%; background: ${t.navBackground}; color: ${t.accent}; border: 1px solid ${t.border}; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; cursor: pointer; font-family: serif; user-select: none;" title="$tourTooltip">i</span>
                    <span id="nav-language" style="padding: 4px 10px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border}; transition: all 0.2s;" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    <span id="nav-guide" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; border: 1px solid ${t.border};" title="${StringKey.TOOLTIP_HOW_IT_WORKS.tr()}">❓</span>
                    <span id="nav-settings" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; border: 1px solid ${t.border};">&#9776;</span>
                </div>
            </div>
            <div style="height: 80px;"></div>
            """
        }
    }

    private fun renderYearOptions(): String {
        val currentYear = Date().getFullYear()
        return (currentYear - 2..currentYear + 2).joinToString("") { year ->
            """<option value="$year" ${if (year == selectedYear) "selected" else ""}>$year</option>"""
        }
    }

    private fun renderSpreadsheet(): String {
        val t = WebTheme.current
        val headerBg = if (t.isDark) "#1a1a1a" else t.background
        val cellBorder = if (t.isDark) "#333" else t.border
        val cellBorderLight = if (t.isDark) "#222" else t.border
        val shadowColor = if (t.isDark) "rgba(0,0,0,0.3)" else "rgba(0,0,0,0.06)"
        val incomeColor = t.incomeGreen
        val expenseColor = t.expenseRed
        // Opaque section header backgrounds (sticky rows can't be transparent)
        val incomeBg = if (t.isDark) "#0d1f0d" else "#EBF0E7"
        val expenseBg = if (t.isDark) "#1f0d0d" else "#F0E9E7"
        val netBg = if (t.isDark) "#0d1a1f" else "#E6EDEE"
        val primaryRowBg = if (t.isDark) "#181818" else t.card
        val secondaryRowBg = if (t.isDark) "#1E1E22" else t.background
        // Sticky positions relative to the scroll container (not the page)
        val headerTop = 0
        val incomeTop = 42
        val expenseTop = 84

        val headerRow = """
            <tr style="background: $headerBg;">
                <th style="padding: 12px 16px; text-align: left; font-weight: 600; color: ${t.textPrimary}; border-bottom: 1px solid $cellBorder; position: sticky; left: 0; top: ${headerTop}px; background: $headerBg; min-width: 160px; z-index: 30; box-shadow: 2px 0 4px $shadowColor;">${StringKey.BUDGET_CATEGORY_CSV.tr()}</th>
                ${monthNames.mapIndexed { index, month ->
                    """<th style="padding: 12px 16px; text-align: center; font-weight: 500; color: ${t.textSecondary}; border-bottom: 1px solid $cellBorder; min-width: 80px; background: $headerBg; position: sticky; top: ${headerTop}px; z-index: 20;">$month</th>"""
                }.joinToString("")}
                <th style="padding: 12px 16px; text-align: right; font-weight: 600; color: ${t.accent}; border-bottom: 1px solid $cellBorder; min-width: 100px; background: $headerBg; position: sticky; top: ${headerTop}px; z-index: 20;">TOTAL</th>
            </tr>
        """

        // Income Section - header with totals
        val incomeIconSvg = CategoryIconSvg.render("cash_forward", size = 18, color = incomeColor)
        val incomeSectionHeader = renderSectionHeaderWithTotals(StringKey.DASHBOARD_INCOME.tr().uppercase(), "income", incomeColor, incomeBg, incomeIconSvg, stickyTop = incomeTop, dataGuide = "income-section")

        val incomeGroups = categoryGroups.filter { it.isIncome }
        val incomeRows = incomeGroups.joinToString("") { group ->
            renderGroupRows(group, "income")
        }

        // Expense Section - header with totals
        val expenseIconSvg = CategoryIconSvg.render("cash_back", size = 18, color = expenseColor)
        val expenseSectionHeader = renderSectionHeaderWithTotals(StringKey.DASHBOARD_EXPENSES.tr().uppercase(), "expense", expenseColor, expenseBg, expenseIconSvg, stickyTop = expenseTop, dataGuide = "expense-section")

        val expenseGroups = categoryGroups.filter { !it.isIncome }
        val expenseRows = expenseGroups.joinToString("") { group ->
            renderGroupRows(group, "expense")
        }

        // Net Savings Row
        val netSavingsRow = renderNetSavingsRow()
        val savingsPercentRow = renderSavingsPercentageRow()

        return """
            <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
                $headerRow
                $incomeSectionHeader
                $incomeRows
                $expenseSectionHeader
                $expenseRows
                $netSavingsRow
                $savingsPercentRow
            </table>
        """
    }

    /**
     * Render section header (INCOME or EXPENSES) with monthly totals and copy-to-next-column button.
     */
    private fun renderSectionHeaderWithTotals(label: String, type: String, color: String, bgColor: String, emoji: String, stickyTop: Int = -1, dataGuide: String = ""): String {
        val t = WebTheme.current
        val cellBorder = if (t.isDark) "#333" else t.border
        val shadowColor = if (t.isDark) "rgba(0,0,0,0.3)" else "rgba(0,0,0,0.06)"
        val data = if (type == "income") incomeData else expenseData
        val tdSticky = if (stickyTop >= 0) "position: sticky; top: ${stickyTop}px; z-index: 19;" else ""

        val monthTotals = (1..12).map { month ->
            val total = data.values.sumOf { it[month] ?: 0.0 }
            val isLastMonth = month == 12
            val copyButton = if (!isLastMonth) {
                """<button
                    class="copy-column-btn"
                    data-type="$type"
                    data-month="$month"
                    style="
                        background: transparent;
                        border: none;
                        color: ${t.textSecondary};
                        cursor: pointer;
                        padding: 2px 4px;
                        font-size: 12px;
                        opacity: 0.5;
                        margin-left: 4px;
                        position: relative;
                    "
                    onmouseover="this.style.opacity='1'; this.style.color='$color';"
                    onmouseout="this.style.opacity='0.5'; this.style.color='${t.textSecondary}';"
                >→<span class="copy-tooltip" style="
                    display: none;
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    background: ${if (t.isDark) "#333" else t.card};
                    color: ${t.textPrimary};
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 10px;
                    white-space: nowrap;
                    margin-bottom: 4px;
                    pointer-events: none;
                    border: 1px solid ${t.border};
                ">alt →</span></button>"""
            } else ""
            """<td style="padding: 8px 12px; text-align: right; color: $color; font-weight: 600; border-bottom: 1px solid $cellBorder; background: $bgColor; font-family: 'SF Mono', 'Monaco', monospace; white-space: nowrap; $tdSticky" data-month-total="$month" data-month-total-type="$type">
                ${formatTotal(total)}$copyButton
            </td>"""
        }.joinToString("")

        val yearTotal = data.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }

        val cellBg = "background: $bgColor;"
        // Corner cell (sticky left + top): needs highest z-index
        val cornerSticky = if (stickyTop >= 0) "position: sticky; left: 0; top: ${stickyTop}px; z-index: 25;" else "position: sticky; left: 0; z-index: 10;"

        val dataGuideAttr = if (dataGuide.isNotEmpty()) "data-guide=\"$dataGuide\"" else ""
        return """
            <tr $dataGuideAttr style="background: $bgColor;">
                <td style="padding: 12px 16px; font-weight: 600; color: $color; border-bottom: 1px solid $cellBorder; $cornerSticky $cellBg box-shadow: 2px 0 4px $shadowColor;">
                    $emoji $label
                </td>
                $monthTotals
                <td style="padding: 12px 16px; text-align: right; color: $color; font-weight: 700; border-bottom: 1px solid $cellBorder; $cellBg $tdSticky font-family: 'SF Mono', 'Monaco', monospace;" data-year-total-type="$type">
                    ${formatTotal(yearTotal)}
                </td>
            </tr>
        """
    }

    /**
     * Render a primary category group row plus its subcategory rows (if expanded).
     */
    private fun renderGroupRows(group: BudgetCategoryGroup, type: String): String {
        val isExpanded = group.code in expandedGroups
        val toggleIcon = if (isExpanded) "▾" else "▸"
        val data = if (type == "income") incomeData else expenseData
        val monthlyData = data[group.code] ?: mutableMapOf()

        // Sub-category totals per month — always computed so the primary
        // row in State B can render its derived display values, and so the
        // year-total column knows what to sum.
        val subTotalsPerMonth: Map<Int, Double> = (1..12).associateWith { month ->
            group.subcategories.sumOf { sub ->
                (data[sub.code] ?: emptyMap())[month] ?: 0.0
            }
        }

        // Source-of-truth state — only meaningful on expense rows. Income
        // doesn't have the primary↔sub model in the same way.
        val sourceMode = if (type == "expense") sourceModeFor(group, type) else SourceMode.EMPTY
        val primaryShowsSubSum = sourceMode == SourceMode.SUBS

        // Primary group row — in State B, a small "Σ sum" label appears
        // next to the name as a visual reminder that this row is a sum.
        val sumLabelHtml = if (primaryShowsSubSum) {
            val t = WebTheme.current
            """<span title="${escapeHtml(StringKey.BUDGET_PRIMARY_SUM_HINT.tr())}" style="
                margin-left: 8px;
                font-size: 10px;
                font-style: italic;
                color: ${t.textSecondary};
                vertical-align: middle;
                white-space: nowrap;
            ">${escapeHtml(StringKey.BUDGET_PRIMARY_SUM_LABEL.tr())}</span>"""
        } else ""

        val primaryRow = renderCategoryRow(
            code = group.code,
            displayName = "${CategoryIconSvg.render(group.iconId, size = 14, color = if (WebTheme.current.isDark) "#ffffff" else "#999999")} $toggleIcon ${group.displayName}$sumLabelHtml",
            type = type,
            monthlyData = monthlyData,
            isPrimary = true,
            subTotalsPerMonth = subTotalsPerMonth,
            primaryShowsSubSum = primaryShowsSubSum,
        )

        // Subcategory rows (only when expanded).
        val subRows = if (isExpanded) {
            group.subcategories.joinToString("") { sub ->
                val subData = data[sub.code] ?: mutableMapOf()
                renderCategoryRow(
                    code = sub.code,
                    displayName = sub.displayName,
                    type = type,
                    monthlyData = subData,
                    isPrimary = false
                )
            }
        } else ""

        return primaryRow + subRows
    }

    private fun renderCategoryRow(
        code: String, displayName: String, type: String,
        monthlyData: Map<Int, Double>, isPrimary: Boolean = false,
        subTotalsPerMonth: Map<Int, Double>? = null,
        /** True when this primary row is "sub-driven" — its monthly cells
         *  should display the sum of sub-category values (greyed + italic)
         *  rather than the primary's own stored amount. */
        primaryShowsSubSum: Boolean = false,
    ): String {
        val t = WebTheme.current
        val cellBorderLight = if (t.isDark) "#222" else t.border
        val shadowColor = if (t.isDark) "rgba(0,0,0,0.3)" else "rgba(0,0,0,0.06)"
        val primaryRowBg = if (t.isDark) "#181818" else t.card
        val secondaryRowBg = if (t.isDark) "#1E1E22" else t.background
        val dataCellBg = if (!isPrimary) "background: $secondaryRowBg;" else ""
        val cellsHtml = (1..12).map { month ->
            // When this primary row is sub-driven, each cell displays the sum
            // of sub-category values for that month instead of the primary's
            // own stored amount. Greyed italic styling signals "this isn't
            // editable as a budget — it's a sum"; the actual input still
            // accepts typing so the user can take over (modal will confirm).
            val rawValue = monthlyData[month] ?: 0.0
            val subSumForMonth = subTotalsPerMonth?.get(month) ?: 0.0
            val value = if (primaryShowsSubSum) subSumForMonth else rawValue
            val displayValue = formatCellValue(value)
            val isLastMonth = month == 12
            val copyButtonColor = if (type == "income") "#32CD32" else "#FF6B6B"
            val copyButton = if (!isLastMonth) {
                """<button
                    class="copy-cell-btn"
                    data-category="$code"
                    data-type="$type"
                    data-month="$month"
                    style="
                        background: rgba(255,255,255,0.1);
                        border: 1px solid $copyButtonColor;
                        border-radius: 3px;
                        color: $copyButtonColor;
                        cursor: pointer;
                        padding: 2px 4px;
                        font-size: 11px;
                        font-weight: bold;
                        opacity: 0;
                        position: absolute;
                        right: 4px;
                        top: 50%;
                        transform: translateY(-50%);
                    "
                >→<span class="copy-tooltip" style="
                    display: none;
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    background: ${if (t.isDark) "#333" else t.card};
                    color: ${t.textPrimary};
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 10px;
                    white-space: nowrap;
                    margin-bottom: 4px;
                    pointer-events: none;
                    border: 1px solid ${t.border};
                ">alt →</span></button>"""
            } else ""
            // State-B (sub-driven primary): cells render the sum greyed +
            // italic to signal "this is a derived total, not an input."
            // Typing into the cell still works but triggers the "replace
            // sub-amounts?" modal on commit.
            val derivedColor = if (primaryShowsSubSum) t.textSecondary else t.textPrimary
            val derivedFontStyle = if (primaryShowsSubSum) "font-style: italic;" else ""
            val derivedTitle = if (primaryShowsSubSum) StringKey.BUDGET_PRIMARY_SUM_HINT.tr() else ""
            """
                <td style="padding: 4px 8px; border-bottom: 1px solid $cellBorderLight; $dataCellBg position: relative;"
                    onmouseover="var btn=this.querySelector('.copy-cell-btn'); if(btn) btn.style.opacity='1';"
                    onmouseout="var btn=this.querySelector('.copy-cell-btn'); if(btn) btn.style.opacity='0';">
                    <input
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,]*"
                        data-category="$code"
                        data-type="$type"
                        data-month="$month"
                        value="$displayValue"
                        placeholder="-"
                        title="$derivedTitle"
                        style="
                            width: calc(100% - 16px);
                            padding: 8px;
                            border: 1px solid transparent;
                            border-radius: 4px;
                            background: transparent;
                            color: $derivedColor;
                            text-align: right;
                            font-size: 14px;
                            outline: none;
                            font-family: 'SF Mono', 'Monaco', monospace;
                            -moz-appearance: textfield;
                            $derivedFontStyle
                        "
                        onfocus="this.style.borderColor='${t.accent}'; this.style.background='${t.background}';"
                        onblur="this.style.borderColor='transparent'; this.style.background='transparent';"
                    />$copyButton
                </td>
            """
        }.joinToString("")

        val total = (1..12).sumOf { monthlyData[it] ?: 0.0 }
        val subYearTotal = subTotalsPerMonth?.values?.sum() ?: 0.0
        val totalColor = if (type == "income") t.incomeGreen else t.expenseRed
        val indent = if (!isPrimary) "padding-left: 32px;" else ""
        val fontWeight = if (isPrimary) "font-weight: 500;" else ""
        val cursor = if (isPrimary) "cursor: pointer;" else ""
        // In State B the primary's own monthly map is empty; the year total
        // is the sum of sub-totals. In State A / EMPTY just the primary total.
        val yearTotalDisplay = if (primaryShowsSubSum) {
            formatTotal(subYearTotal)
        } else {
            formatTotal(total)
        }

        return """
            <tr data-row-category="$code" data-row-type="$type" ${if (isPrimary) """data-group-code="$code"""" else ""}>
                <td style="padding: 8px 16px; $indent $fontWeight $cursor color: ${t.textPrimary}; border-bottom: 1px solid $cellBorderLight; position: sticky; left: 0; background: $primaryRowBg; z-index: 10; box-shadow: 2px 0 4px $shadowColor;" ${if (isPrimary) """class="group-toggle" data-group-code="$code"""" else ""}>$displayName</td>
                $cellsHtml
                <td style="padding: 8px 16px; text-align: right; color: $totalColor; font-weight: 500; border-bottom: 1px solid $cellBorderLight; $dataCellBg font-family: 'SF Mono', 'Monaco', monospace;" data-total-category="$code" data-total-type="$type">
                    $yearTotalDisplay
                </td>
            </tr>
        """
    }

    private fun renderNetSavingsRow(): String {
        val t = WebTheme.current
        val cellBorder = if (t.isDark) "#333" else t.border
        val netBg = if (t.isDark) "#0d1a1f" else "#E6EDEE"
        val shadowColor = if (t.isDark) "rgba(0,0,0,0.3)" else "rgba(0,0,0,0.06)"
        val monthCells = (1..12).map { month ->
            val income = incomeData.values.sumOf { it[month] ?: 0.0 }
            val expenses = expenseData.values.sumOf { it[month] ?: 0.0 }
            val net = income - expenses
            val color = if (net >= 0) t.accent else t.expenseRed
            val prefix = if (net < 0) "-" else ""
            """<td style="padding: 12px 16px; text-align: right; color: $color; font-weight: 600; border-bottom: 1px solid $cellBorder; background: $netBg; font-family: 'SF Mono', 'Monaco', monospace;" data-net-savings-month="$month">
                ${if (net == 0.0) "-" else "$prefix$${kotlin.math.round(kotlin.math.abs(net)).toInt()}"}
            </td>"""
        }.joinToString("")

        val yearIncome = incomeData.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }
        val yearExpenses = expenseData.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }
        val yearNet = yearIncome - yearExpenses
        val yearColor = if (yearNet >= 0) t.accent else t.expenseRed
        val yearPrefix = if (yearNet < 0) "-" else ""

        return """
            <tr data-guide="net-savings-row" style="background: $netBg;">
                <td style="padding: 12px 16px; font-weight: 600; color: ${t.accent}; border-bottom: 1px solid $cellBorder; position: sticky; left: 0; background: $netBg; z-index: 10; box-shadow: 2px 0 4px $shadowColor;">${StringKey.BUDGET_NET_SAVINGS.tr()}</td>
                $monthCells
                <td style="padding: 12px 16px; text-align: right; color: $yearColor; font-weight: 700; border-bottom: 1px solid $cellBorder; background: $netBg; font-family: 'SF Mono', 'Monaco', monospace;" data-year-net-savings>
                    ${if (yearNet == 0.0) "-" else "$yearPrefix$${kotlin.math.round(kotlin.math.abs(yearNet)).toInt()}"}
                </td>
            </tr>
        """
    }

    private fun renderSavingsPercentageRow(): String {
        val t = WebTheme.current
        val cellBorder = if (t.isDark) "#333" else t.border
        val rowBg = t.card
        val shadowColor = if (t.isDark) "rgba(0,0,0,0.3)" else "rgba(0,0,0,0.06)"
        val monthCells = (1..12).map { month ->
            val income = incomeData.values.sumOf { it[month] ?: 0.0 }
            val expenses = expenseData.values.sumOf { it[month] ?: 0.0 }
            val percentage = if (income > 0) ((income - expenses) / income * 100) else 0.0
            val color = when {
                percentage >= 20 -> t.incomeGreen
                percentage >= 0 -> t.targetOrange
                else -> t.expenseRed
            }
            """<td style="padding: 12px 16px; text-align: right; color: $color; font-weight: 500; border-bottom: 1px solid $cellBorder; background: $rowBg; font-family: 'SF Mono', 'Monaco', monospace;" data-savings-pct-month="$month">
                ${if (income == 0.0) "-" else "${round(percentage).toInt()}%"}
            </td>"""
        }.joinToString("")

        val yearIncome = incomeData.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }
        val yearExpenses = expenseData.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }
        val yearPercentage = if (yearIncome > 0) ((yearIncome - yearExpenses) / yearIncome * 100) else 0.0
        val yearColor = when {
            yearPercentage >= 20 -> t.incomeGreen
            yearPercentage >= 0 -> t.targetOrange
            else -> t.expenseRed
        }

        return """
            <tr>
                <td style="padding: 12px 16px; font-weight: 500; color: ${t.textSecondary}; border-bottom: 1px solid $cellBorder; position: sticky; left: 0; background: $rowBg; z-index: 10; box-shadow: 2px 0 4px $shadowColor;">${StringKey.BUDGET_SAVINGS_PERCENT.tr()}</td>
                $monthCells
                <td style="padding: 12px 16px; text-align: right; color: $yearColor; font-weight: 600; border-bottom: 1px solid $cellBorder; background: $rowBg; font-family: 'SF Mono', 'Monaco', monospace;" data-year-savings-pct>
                    ${if (yearIncome == 0.0) "-" else "${round(yearPercentage).toInt()}%"}
                </td>
            </tr>
        """
    }

    private fun setupEventListeners(container: HTMLElement) {
        // Logo click - navigate to Landing page
        container.querySelector("#budget-logo")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.Landing)
        })

        // "How Wiz3r works" button → modal overlay
        container.querySelector("#nav-guide")?.addEventListener("click", {
            showGuideModal()
        })

        // Tour button
        GuideOverlay.ensureStylesInstalled()
        container.querySelector("#nav-tour")?.addEventListener("click", {
            // Tag the first month-1 input's parent td as the "budget-cell" anchor.
            // Input rows are re-rendered on year change, so tag at tour time.
            val firstInput = container.querySelector("input[data-month='1']") as? HTMLElement
            firstInput?.parentElement?.setAttribute("data-guide", "budget-cell")
            GuideOverlay(guide.GuideScreen.BUDGET).start()
            (container.querySelector("#nav-tour") as? HTMLElement)?.classList?.remove("wiz3r-guide-halo")
        })

        // Marketing-capture demo mode (?demo=1): auto-trigger 'i' click.
        autoStartTourIfDemoMode("#nav-tour")

        // Year selector
        container.querySelector("#yearSelector")?.addEventListener("change", { event ->
            val select = event.target as? HTMLSelectElement
            select?.value?.toIntOrNull()?.let { year ->
                // Data is already saved to server per-cell, just switch year
                selectedYear = year
                // Clear in-memory data and reload for new year from server
                clearAndReloadData()
            }
        })

        // Copy from previous year
        container.querySelector("#copyPrevYear")?.addEventListener("click", {
            copyFromPreviousYear()
        })

        // Export CSV
        container.querySelector("#exportBtn")?.addEventListener("click", {
            exportToCSV()
        })

        // Cell input handlers — route through the transition checker so that
        // committing a value on a sub-cell when the primary has data (or vice
        // versa) raises a confirmation modal before mutating storage.
        val inputs = container.querySelectorAll("input[data-category]")
        for (i in 0 until inputs.length) {
            val input = inputs.item(i) as? HTMLInputElement ?: continue
            input.addEventListener("change", { event ->
                val inputEl = event.target as? HTMLInputElement ?: return@addEventListener
                val category = inputEl.getAttribute("data-category") ?: return@addEventListener
                val type = inputEl.getAttribute("data-type") ?: return@addEventListener
                val month = inputEl.getAttribute("data-month")?.toIntOrNull() ?: return@addEventListener
                val value = parseInputValue(inputEl.value)
                val data = if (type == "income") incomeData else expenseData
                val previousValue = data[category]?.get(month) ?: 0.0

                handleCellCommitWithTransitionCheck(category, type, month, value, inputEl, previousValue)
            })
        }

        // Navigation listeners
        setupNavListeners(container)

        // Scroll listener for navbar shrink — only runs while this component is in the DOM
        window.addEventListener("scroll", { _ ->
            if (!document.body!!.contains(container)) return@addEventListener
            val scrollY = window.scrollY
            val logo = document.getElementById("budget-logo") as? org.w3c.dom.HTMLElement
            val navbar = logo?.parentElement as? org.w3c.dom.HTMLElement
            val progress = (scrollY / 80.0).coerceIn(0.0, 1.0)
            val vPad = (16 - (8 * progress)).toInt()
            val logoH = (56 - (28 * progress)).toInt()
            navbar?.style?.setProperty("padding", "${vPad}px 32px")
            logo?.style?.setProperty("height", "${logoH}px")
        })
    }

    private fun setupNavListeners(container: HTMLElement) {
        container.querySelector("#nav-activity")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
        })
        container.querySelector("#nav-dashboard")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
        })
        container.querySelector("#nav-goals")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.GOALS)
        })
        container.querySelector("#nav-budget")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
        })
        // Language toggle - quick switch between languages
        container.querySelector("#nav-language")?.addEventListener("click", {
            LocaleManager.toggleLocale()
            window.location.reload()
        })
    }

    private fun setupMobileMenuListeners(container: HTMLElement) {
        val mobileMenuOverlay = container.querySelector("#mobileMenuOverlay") as? HTMLElement
        val mobileMenuBackdrop = container.querySelector("#mobileMenuBackdrop") as? HTMLElement

        // Hamburger menu open
        container.querySelector("#hamburgerMenu")?.addEventListener("click", {
            mobileMenuOverlay?.style?.display = "block"
            mobileMenuBackdrop?.style?.display = "block"
        })

        // Delegate to shared mobile menu
        MarketingMenu.attachMobileEventListeners(
            container = container,
            onNavigate = { page ->
                if (page == "guide") {
                    showGuideModal()
                    return@attachMobileEventListeners
                }
                val screen = when (page) {
                    "team" -> Screen.Team
                    "about" -> Screen.About
                    "contact" -> Screen.Contact
                    "subscribe" -> Screen.Subscribe()
                    "privacy" -> Screen.Privacy
                    "terms" -> Screen.Terms
                    "security" -> Screen.Security
                    else -> Screen.Landing
                }
                navigationManager.navigateTo(screen)
            },
            onAppNavigate = { tab ->
                when (tab) {
                    "transactions" -> navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
                    "dashboard" -> navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
                    "goals" -> navigationManager.switchToTab(ui.AppTabType.GOALS)
                    "budget" -> navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
                    "settings" -> navigationManager.switchToTab(ui.AppTabType.SETTINGS)
                    // An actual logout — see WebDashboardRedesigned: navigating to
                    // Landing left the stored token intact, so the visitor was
                    // never signed out.
                    "logout" -> navigationManager.logout()
                }
            }
        )
    }

    private fun updateCell(category: String, type: String, month: Int, value: Double) {
        val data = if (type == "income") incomeData else expenseData

        if (!data.containsKey(category)) {
            data[category] = mutableMapOf()
        }
        data[category]!![month] = value

        // Save to server API for cross-platform sync
        saveToServer(category, type, month, value)
        updateTotals()
    }

    /**
     * Wrap a cell commit with the primary↔sub source-of-truth check.
     *
     * Returns true if the value was applied immediately; false if a modal
     * is open and the caller should NOT update its own UI state yet (the
     * modal callbacks will trigger a re-render once the user decides).
     *
     * Only fires for expense rows — income & transfers don't have the
     * primary/sub source-of-truth model in the same way.
     */
    private fun handleCellCommitWithTransitionCheck(
        category: String,
        type: String,
        month: Int,
        value: Double,
        inputEl: HTMLInputElement,
        previousValue: Double,
    ): Boolean {
        // 0 / negative values just zero out — never a transition trigger.
        if (value <= 0.0) {
            updateCell(category, type, month, value)
            return true
        }
        // Income rows are simpler — no source-of-truth model needed.
        if (type != "expense") {
            updateCell(category, type, month, value)
            return true
        }

        val isPrimaryEdit = isPrimaryCode(category)
        val group: BudgetCategoryGroup = if (isPrimaryEdit) {
            categoryGroups.firstOrNull { it.code == category } ?: run {
                updateCell(category, type, month, value); return true
            }
        } else {
            parentGroupFor(category) ?: run {
                // Custom sub or no parent — just save.
                updateCell(category, type, month, value); return true
            }
        }
        val mode = sourceModeFor(group, type)

        // Case A → B: primary was the source, user typed into a sub.
        if (!isPrimaryEdit && mode == SourceMode.PRIMARY) {
            showSwitchToSubModal(group, sub = category, type = type, month = month, value = value, inputEl = inputEl, previousValue = previousValue)
            return false
        }
        // Case B → A: subs were the source, user typed into the primary.
        if (isPrimaryEdit && mode == SourceMode.SUBS) {
            showSwitchToPrimaryModal(group, type = type, month = month, value = value, inputEl = inputEl, previousValue = previousValue)
            return false
        }

        // No transition — straightforward update.
        updateCell(category, type, month, value)
        return true
    }

    /** A → B confirmation modal: switching to sub-category budgets. */
    private fun showSwitchToSubModal(
        group: BudgetCategoryGroup,
        sub: String,
        type: String,
        month: Int,
        value: Double,
        inputEl: HTMLInputElement,
        previousValue: Double,
    ) {
        val data = if (type == "income") incomeData else expenseData
        showTransitionModal(
            title = StringKey.BUDGET_SWITCH_TO_SUB_TITLE.tr(),
            body = StringKey.BUDGET_SWITCH_TO_SUB_BODY.tr(group.displayName),
            confirmLabel = StringKey.BUDGET_SWITCH_TO_SUB_CONFIRM.tr(),
            onConfirm = {
                // Wipe the primary row's monthly data locally + on server.
                data[group.code]?.clear()
                GlobalScope.launch {
                    try {
                        apiService.deleteBudgetPlanCategory(selectedYear, type, group.code)
                    } catch (e: Exception) {
                        console.warn("Failed to clear primary budget: ${e.message}")
                    }
                }
                // Save the new sub value.
                updateCell(sub, type, month, value)
                refreshSpreadsheet()
            },
            onCancel = {
                inputEl.value = formatCellValue(previousValue)
            },
        )
    }

    /**
     * B → A confirmation modal: switching back to a primary-level budget.
     *
     * Preserves data: for each month, the sub-category SUM migrates to the
     * primary's monthly value. The user's just-edited month gets the new
     * value they typed (overrides the migrated sum for that month). Then
     * the sub-category data is wiped — locally and on the server.
     *
     * Without the migration, sub-amounts for months the user didn't touch
     * would just disappear (Eric's bug report).
     */
    private fun showSwitchToPrimaryModal(
        group: BudgetCategoryGroup,
        type: String,
        month: Int,
        value: Double,
        inputEl: HTMLInputElement,
        previousValue: Double,
    ) {
        val data = if (type == "income") incomeData else expenseData
        showTransitionModal(
            title = StringKey.BUDGET_SWITCH_TO_PRIMARY_TITLE.tr(),
            body = StringKey.BUDGET_SWITCH_TO_PRIMARY_BODY.tr(group.displayName),
            confirmLabel = StringKey.BUDGET_SWITCH_TO_PRIMARY_CONFIRM.tr(),
            onConfirm = {
                // Snapshot sub-sums BEFORE wiping subs so we have a clean
                // baseline to migrate to the primary.
                val subSumByMonth: Map<Int, Double> = (1..12).associateWith { m ->
                    group.subcategories.sumOf { sub ->
                        (data[sub.code] ?: emptyMap())[m] ?: 0.0
                    }
                }

                // Migrate sums to the primary's monthly map for every month
                // that had non-zero sub data. Server-side save runs per month.
                if (!data.containsKey(group.code)) data[group.code] = mutableMapOf()
                subSumByMonth.forEach { (m, sum) ->
                    if (sum > 0.0) {
                        data[group.code]!![m] = sum
                        saveToServer(group.code, type, m, sum)
                    }
                }

                // Wipe sub-category data locally + on server.
                group.subcategories.forEach { sub ->
                    data[sub.code]?.clear()
                    GlobalScope.launch {
                        try {
                            apiService.deleteBudgetPlanCategory(selectedYear, type, sub.code)
                        } catch (e: Exception) {
                            console.warn("Failed to clear sub budget ${sub.code}: ${e.message}")
                        }
                    }
                }

                // Apply the user's new value last so it overrides whatever
                // migrated sum was already on the primary for this month.
                updateCell(group.code, type, month, value)
                refreshSpreadsheet()
            },
            onCancel = {
                inputEl.value = formatCellValue(previousValue)
            },
        )
    }

    /** Centered modal with title, body, and Cancel / Confirm buttons. */
    private fun showTransitionModal(
        title: String,
        body: String,
        confirmLabel: String,
        onConfirm: () -> Unit,
        onCancel: () -> Unit,
    ) {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.7);
            z-index: 2000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
        """.trimIndent()

        val frame = document.createElement("div") as HTMLElement
        frame.style.cssText = """
            background: ${t.card};
            border: 1px solid ${t.border};
            border-radius: 12px;
            box-shadow: 0 18px 60px rgba(0,0,0,0.6);
            max-width: 480px;
            width: 100%;
            padding: 22px;
        """.trimIndent()

        frame.innerHTML = """
            <h3 style="margin: 0 0 12px 0; color: ${t.textPrimary}; font-size: 17px;">${escapeHtml(title)}</h3>
            <p style="margin: 0 0 18px 0; color: ${t.textSecondary}; font-size: 14px; line-height: 1.5;">${escapeHtml(body)}</p>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
                <button id="budget-modal-cancel" style="
                    padding: 8px 14px;
                    background: transparent;
                    border: 1px solid ${t.border};
                    color: ${t.textPrimary};
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 13px;
                ">${escapeHtml(StringKey.BUDGET_TRANSITION_CANCEL.tr())}</button>
                <button id="budget-modal-confirm" style="
                    padding: 8px 14px;
                    background: #ef4444;
                    border: 1px solid #dc2626;
                    color: #fff;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 500;
                ">${escapeHtml(confirmLabel)}</button>
            </div>
        """.trimIndent()

        overlay.appendChild(frame)
        document.body?.appendChild(overlay)

        val close: () -> Unit = {
            overlay.parentNode?.removeChild(overlay)
        }
        (frame.querySelector("#budget-modal-cancel") as? HTMLElement)?.onclick = { _ ->
            close(); onCancel()
            Unit
        }
        (frame.querySelector("#budget-modal-confirm") as? HTMLElement)?.onclick = { _ ->
            close(); onConfirm()
            Unit
        }
        // Click outside the frame → treat as cancel
        overlay.onclick = { event ->
            if (event.target == overlay) { close(); onCancel() }
        }
    }

    /** Delegates to the shared [HtmlEscape]; kept as a member so call sites read unchanged. */
    private fun escapeHtml(s: String): String = HtmlEscape.escape(s)

    private fun updateTotals() {
        val cont = container ?: return

        // Update category totals
        listOf("income" to incomeData, "expense" to expenseData).forEach { (type, data) ->
            data.forEach { (category, monthlyData) ->
                val total = (1..12).sumOf { monthlyData[it] ?: 0.0 }
                cont.querySelector("[data-total-category='$category'][data-total-type='$type']")?.let { el ->
                    (el as HTMLElement).textContent = formatTotal(total)
                }
            }

            // Update month totals (preserve copy button)
            (1..12).forEach { month ->
                val monthTotal = data.values.sumOf { it[month] ?: 0.0 }
                cont.querySelector("[data-month-total='$month'][data-month-total-type='$type']")?.let { el ->
                    val htmlEl = el as HTMLElement
                    val copyBtn = htmlEl.querySelector(".copy-column-btn")
                    val totalText = formatTotal(monthTotal)
                    // Update text before button or set as only content
                    if (copyBtn != null) {
                        htmlEl.firstChild?.textContent = totalText
                    } else {
                        htmlEl.textContent = totalText
                    }
                }
            }

            // Update year total
            val yearTotal = data.values.sumOf { categoryData ->
                (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
            }
            cont.querySelector("[data-year-total-type='$type']")?.let { el ->
                (el as HTMLElement).textContent = formatTotal(yearTotal)
            }
        }

        // Update net savings
        (1..12).forEach { month ->
            val income = incomeData.values.sumOf { it[month] ?: 0.0 }
            val expenses = expenseData.values.sumOf { it[month] ?: 0.0 }
            val net = income - expenses
            val prefix = if (net < 0) "-" else ""
            cont.querySelector("[data-net-savings-month='$month']")?.let { el ->
                val htmlEl = el as HTMLElement
                htmlEl.textContent = if (net == 0.0) "-" else "$prefix$${kotlin.math.round(kotlin.math.abs(net)).toInt()}"
                htmlEl.style.color = if (net >= 0) WebTheme.current.accent else WebTheme.current.expenseRed
            }

            // Update savings percentage
            val percentage = if (income > 0) ((income - expenses) / income * 100) else 0.0
            cont.querySelector("[data-savings-pct-month='$month']")?.let { el ->
                val htmlEl = el as HTMLElement
                htmlEl.textContent = if (income == 0.0) "-" else "${round(percentage).toInt()}%"
                htmlEl.style.color = when {
                    percentage >= 20 -> WebTheme.current.incomeGreen
                    percentage >= 0 -> WebTheme.current.targetOrange
                    else -> WebTheme.current.expenseRed
                }
            }
        }

        // Update year net savings
        val yearIncome = incomeData.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }
        val yearExpenses = expenseData.values.sumOf { categoryData ->
            (1..12).sumOf { month -> categoryData[month] ?: 0.0 }
        }
        val yearNet = yearIncome - yearExpenses
        val yearPrefix = if (yearNet < 0) "-" else ""
        cont.querySelector("[data-year-net-savings]")?.let { el ->
            val htmlEl = el as HTMLElement
            htmlEl.textContent = if (yearNet == 0.0) "-" else "$yearPrefix$${kotlin.math.round(kotlin.math.abs(yearNet)).toInt()}"
            htmlEl.style.color = if (yearNet >= 0) WebTheme.current.accent else WebTheme.current.expenseRed
        }

        // Update year savings percentage
        val yearPercentage = if (yearIncome > 0) ((yearIncome - yearExpenses) / yearIncome * 100) else 0.0
        cont.querySelector("[data-year-savings-pct]")?.let { el ->
            val htmlEl = el as HTMLElement
            htmlEl.textContent = if (yearIncome == 0.0) "-" else "${round(yearPercentage).toInt()}%"
            htmlEl.style.color = when {
                yearPercentage >= 20 -> WebTheme.current.incomeGreen
                yearPercentage >= 0 -> WebTheme.current.targetOrange
                else -> WebTheme.current.expenseRed
            }
        }
    }

    private fun refreshSpreadsheet() {
        val cont = container ?: return
        cont.querySelector("#budgetSpreadsheet")?.let { spreadsheet ->
            (spreadsheet as HTMLElement).innerHTML = renderSpreadsheet()
            setupCellListeners(cont)
            maybeFocusCategory(cont)
        }
        cont.querySelector("#copyPrevYear")?.let { btn ->
            (btn as HTMLElement).textContent = StringKey.BUDGET_COPY_FROM_YEAR.tr((selectedYear - 1).toString())
        }
    }

    /** One-shot focus on the row for the category code stashed by a Wiz 3R
     *  "Set a budget for this category" action. Expands the parent group if
     *  the row is a sub-category, scrolls it into view, and pulses a colored
     *  background on every cell of the row.
     *
     *  `outline` on a `<tr>` is rendered inconsistently across browsers in
     *  tables (Safari often ignores it) and the sticky first cell with its
     *  own opaque background can clip the outline besides. Applying a
     *  background highlight per `<td>` is reliable everywhere. */
    private fun maybeFocusCategory(cont: HTMLElement) {
        val code = navigationManager.consumePendingBudgetFocusCategory() ?: return
        // If the code is a primary that owns a collapsed group with sub-rows,
        // the primary row itself is always visible — no expand needed.
        // For a detailed code, expand its parent group first.
        val parentPrimary = categoryGroups.firstOrNull { group ->
            group.subcategories.any { it.code.equals(code, ignoreCase = true) }
        }
        if (parentPrimary != null && parentPrimary.code !in expandedGroups) {
            expandedGroups += parentPrimary.code
            cont.querySelector("#budgetSpreadsheet")?.let {
                (it as HTMLElement).innerHTML = renderSpreadsheet()
                setupCellListeners(cont)
            }
        }
        val row = cont.querySelector("tr[data-row-category=\"$code\"]") as? HTMLElement ?: return

        // Inject a one-shot stylesheet (idempotent — keyed by id).
        val styleId = "wiz3r-budget-focus-style"
        if (document.getElementById(styleId) == null) {
            val style = document.createElement("style") as HTMLElement
            style.id = styleId
            val accent = WebTheme.current.accent
            style.textContent = """
                @keyframes wiz3rBudgetFocusPulse {
                  0%   { background-color: ${accent}66; }
                  60%  { background-color: ${accent}33; }
                  100% { background-color: transparent; }
                }
                tr.wiz3r-budget-focus > td {
                  animation: wiz3rBudgetFocusPulse 4s ease-out forwards;
                }
            """.trimIndent()
            document.head?.appendChild(style)
        }

        // Defer one frame so the browser has actually painted the row before we
        // scroll — otherwise scrollIntoView lands on a stale layout when the
        // group was just expanded.
        window.setTimeout({
            row.scrollIntoView(jsJson("behavior" to "smooth", "block" to "center"))
            row.classList.add("wiz3r-budget-focus")
            // Strip the class once the animation has played so a later focus
            // request on the same row will re-trigger it.
            window.setTimeout({ row.classList.remove("wiz3r-budget-focus") }, 4200)
        }, 50)
    }

    private fun setupCellListeners(container: HTMLElement) {
        val inputs = container.querySelectorAll("input[data-category]")
        for (i in 0 until inputs.length) {
            val input = inputs.item(i) as? HTMLInputElement ?: continue

            // Value change handler — routes through the primary↔sub
            // source-of-truth transition check so a re-rendered cell still
            // shows the confirmation modal when the user crosses the
            // boundary. Without this, refreshSpreadsheet() re-attached the
            // older bypass handler and edits leaked through silently.
            input.addEventListener("change", { event ->
                val inputEl = event.target as? HTMLInputElement ?: return@addEventListener
                val category = inputEl.getAttribute("data-category") ?: return@addEventListener
                val type = inputEl.getAttribute("data-type") ?: return@addEventListener
                val month = inputEl.getAttribute("data-month")?.toIntOrNull() ?: return@addEventListener
                val value = parseInputValue(inputEl.value)
                val data = if (type == "income") incomeData else expenseData
                val previousValue = data[category]?.get(month) ?: 0.0

                handleCellCommitWithTransitionCheck(category, type, month, value, inputEl, previousValue)
            })

            // Keyboard navigation handler
            input.addEventListener("keydown", { event ->
                val keyEvent = event.asDynamic()
                val inputEl = event.target as? HTMLInputElement ?: return@addEventListener
                val category = inputEl.getAttribute("data-category") ?: return@addEventListener
                val type = inputEl.getAttribute("data-type") ?: return@addEventListener
                val month = inputEl.getAttribute("data-month")?.toIntOrNull() ?: return@addEventListener

                when (keyEvent.key as String) {
                    "ArrowRight" -> {
                        if (keyEvent.altKey == true) {
                            // Alt+Right: Commit current value first, then copy to next cell
                            event.preventDefault()
                            val currentValue = parseInputValue(inputEl.value)
                            updateCell(category, type, month, currentValue)
                            copyToNextCell(category, type, month)
                        } else if (inputEl.selectionStart == inputEl.value.length) {
                            // Navigate right when cursor at end
                            event.preventDefault()
                            navigateToCell(category, type, month + 1)
                        }
                    }
                    "ArrowLeft" -> {
                        if (keyEvent.altKey == true) {
                            // Alt+Left: Commit current value first, then copy to previous cell
                            event.preventDefault()
                            val currentValue = parseInputValue(inputEl.value)
                            updateCell(category, type, month, currentValue)
                            copyToPrevCell(category, type, month)
                        } else if (inputEl.selectionStart == 0) {
                            // Navigate left when cursor at start
                            event.preventDefault()
                            navigateToCell(category, type, month - 1)
                        }
                    }
                    "ArrowDown" -> {
                        event.preventDefault()
                        navigateVertically(category, type, month, 1)
                    }
                    "ArrowUp" -> {
                        event.preventDefault()
                        navigateVertically(category, type, month, -1)
                    }
                    "Enter" -> {
                        event.preventDefault()
                        navigateVertically(category, type, month, 1)
                    }
                    "Tab" -> {
                        // Let Tab navigate naturally but wrap if needed
                        if (!keyEvent.shiftKey && month == 12) {
                            event.preventDefault()
                            navigateVertically(category, type, 1, 1)
                        } else if (keyEvent.shiftKey == true && month == 1) {
                            event.preventDefault()
                            navigateVertically(category, type, 12, -1)
                        }
                    }
                }
            })
        }

        // Copy-to-next cell button handlers
        val copyBtns = container.querySelectorAll(".copy-cell-btn")
        for (i in 0 until copyBtns.length) {
            val btn = copyBtns.item(i) as? HTMLElement ?: continue
            var tooltipTimeout: Int? = null

            btn.addEventListener("click", { event ->
                event.preventDefault()
                event.stopPropagation()
                val category = btn.getAttribute("data-category") ?: return@addEventListener
                val type = btn.getAttribute("data-type") ?: return@addEventListener
                val month = btn.getAttribute("data-month")?.toIntOrNull() ?: return@addEventListener
                copyToNextCell(category, type, month)
            })

            // Show tooltip after 600ms delay
            btn.addEventListener("mouseenter", {
                tooltipTimeout = window.setTimeout({
                    val tooltip = btn.querySelector(".copy-tooltip") as? HTMLElement
                    tooltip?.style?.display = "block"
                }, 600)
            })

            // Hide tooltip and clear pending timeout
            btn.addEventListener("mouseleave", {
                tooltipTimeout?.let { window.clearTimeout(it) }
                tooltipTimeout = null
                val tooltip = btn.querySelector(".copy-tooltip") as? HTMLElement
                tooltip?.style?.display = "none"
            })
        }

        // Copy entire column button handlers (on section headers)
        val copyColumnBtns = container.querySelectorAll(".copy-column-btn")
        for (i in 0 until copyColumnBtns.length) {
            val btn = copyColumnBtns.item(i) as? HTMLElement ?: continue
            var colTooltipTimeout: Int? = null

            btn.addEventListener("click", { event ->
                event.preventDefault()
                event.stopPropagation()
                val type = btn.getAttribute("data-type") ?: return@addEventListener
                val month = btn.getAttribute("data-month")?.toIntOrNull() ?: return@addEventListener
                copyColumnToNext(type, month)
            })

            // Show tooltip after 600ms delay
            btn.addEventListener("mouseenter", {
                colTooltipTimeout = window.setTimeout({
                    val tooltip = btn.querySelector(".copy-tooltip") as? HTMLElement
                    tooltip?.style?.display = "block"
                }, 600)
            })

            // Hide tooltip and clear pending timeout
            btn.addEventListener("mouseleave", {
                colTooltipTimeout?.let { window.clearTimeout(it) }
                colTooltipTimeout = null
                val tooltip = btn.querySelector(".copy-tooltip") as? HTMLElement
                tooltip?.style?.display = "none"
            })
        }

        // Group toggle (expand/collapse) handlers
        val groupToggles = container.querySelectorAll(".group-toggle")
        for (i in 0 until groupToggles.length) {
            val td = groupToggles.item(i) as? HTMLElement ?: continue
            td.addEventListener("click", {
                val groupCode = td.getAttribute("data-group-code") ?: return@addEventListener
                if (groupCode in expandedGroups) {
                    expandedGroups.remove(groupCode)
                } else {
                    expandedGroups.add(groupCode)
                }
                refreshSpreadsheet()
            })
        }
    }

    /**
     * Navigate to a specific cell by category, type, and month
     */
    private fun navigateToCell(category: String, type: String, month: Int) {
        if (month < 1 || month > 12) return
        val cont = container ?: return
        val targetInput = cont.querySelector("input[data-category='$category'][data-type='$type'][data-month='$month']") as? HTMLInputElement
        targetInput?.focus()
        targetInput?.select()
    }

    /**
     * Navigate vertically (up/down) through rows
     */
    /**
     * Returns the list of visible category codes for a given type (income/expense),
     * respecting expanded/collapsed state of groups.
     */
    private fun getVisibleCategories(type: String): List<String> {
        val isIncome = type == "income"
        val groups = categoryGroups.filter { it.isIncome == isIncome }
        val result = mutableListOf<String>()
        for (group in groups) {
            result.add(group.code)
            if (group.code in expandedGroups) {
                group.subcategories.forEach { result.add(it.code) }
            }
        }
        return result
    }

    /**
     * Returns ALL category codes (primary + subcategory) for a given type,
     * regardless of expanded state.
     */
    private fun getAllCategoryCodes(type: String): List<String> {
        val isIncome = type == "income"
        val groups = categoryGroups.filter { it.isIncome == isIncome }
        val result = mutableListOf<String>()
        for (group in groups) {
            result.add(group.code)
            group.subcategories.forEach { result.add(it.code) }
        }
        return result
    }

    private fun navigateVertically(currentCategory: String, type: String, month: Int, direction: Int) {
        val cont = container ?: return
        val categories = getVisibleCategories(type)
        val currentIndex = categories.indexOf(currentCategory)
        if (currentIndex == -1) return

        val targetIndex = currentIndex + direction
        if (targetIndex >= 0 && targetIndex < categories.size) {
            // Navigate within same section
            val targetCategory = categories[targetIndex]
            navigateToCell(targetCategory, type, month)
        } else if (targetIndex < 0 && type == "expense") {
            // Move from first expense to last income
            val incomeCategories = getVisibleCategories("income")
            val lastIncome = incomeCategories.lastOrNull() ?: return
            navigateToCell(lastIncome, "income", month)
        } else if (targetIndex >= categories.size && type == "income") {
            // Move from last income to first expense
            val expenseCategories = getVisibleCategories("expense")
            val firstExpense = expenseCategories.firstOrNull() ?: return
            navigateToCell(firstExpense, "expense", month)
        }
    }

    /**
     * Copy value from current cell to next month
     */
    private fun copyToNextCell(category: String, type: String, month: Int) {
        if (month >= 12) return
        val data = if (type == "income") incomeData else expenseData
        val value = data[category]?.get(month) ?: 0.0

        // Update the next month
        updateCell(category, type, month + 1, value)

        // Update the input field visually
        val cont = container ?: return
        val nextInput = cont.querySelector("input[data-category='$category'][data-type='$type'][data-month='${month + 1}']") as? HTMLInputElement
        nextInput?.value = formatCellValue(value)
        nextInput?.focus()
        nextInput?.select()
    }

    /**
     * Copy value from current cell to previous month
     */
    private fun copyToPrevCell(category: String, type: String, month: Int) {
        if (month <= 1) return
        val data = if (type == "income") incomeData else expenseData
        val value = data[category]?.get(month) ?: 0.0

        // Update the previous month
        updateCell(category, type, month - 1, value)

        // Update the input field visually
        val cont = container ?: return
        val prevInput = cont.querySelector("input[data-category='$category'][data-type='$type'][data-month='${month - 1}']") as? HTMLInputElement
        prevInput?.value = formatCellValue(value)
        prevInput?.focus()
        prevInput?.select()
    }

    /**
     * Copy entire column (all categories of a type) to next month
     */
    private fun copyColumnToNext(type: String, month: Int) {
        if (month >= 12) return
        val data = if (type == "income") incomeData else expenseData
        val categories = getVisibleCategories(type)

        categories.forEach { category ->
            val value = data[category]?.get(month) ?: 0.0
            if (value > 0) {
                updateCell(category, type, month + 1, value)

                // Update the input field visually
                val cont = container ?: return@forEach
                val nextInput = cont.querySelector("input[data-category='$category'][data-type='$type'][data-month='${month + 1}']") as? HTMLInputElement
                nextInput?.value = formatCellValue(value)
            }
        }
    }

    private fun copyFromPreviousYear() {
        val sharedToken = ApiService.getSharedAuthToken()
        val sharedUserId = ApiService.getSharedUserId()

        if (sharedToken == null || sharedUserId == null) {
            showAppAlert(StringKey.BUDGET_LOGIN_TO_COPY.tr())
            return
        }

        // Ensure session is restored to this instance
        if (!apiService.isAuthenticated()) {
            apiService.restoreSession(sharedToken, sharedUserId)
        }

        val prevYear = selectedYear - 1
        GlobalScope.launch {
            try {
                apiService.copyBudgetPlan(prevYear, selectedYear)
                console.log("Successfully copied budget from $prevYear to $selectedYear")

                // Reload the current year's data
                loadFromServer()

                showAppAlert(StringKey.BUDGET_COPY_SUCCESS.tr(prevYear.toString(), selectedYear.toString()))
            } catch (e: Exception) {
                console.error("Failed to copy budget:", e.message)
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                } else {
                    showAppAlert("Failed to copy budget: ${e.message}")
                }
            }
        }
    }

    private fun exportToCSV() {
        val lines = mutableListOf<String>()

        // Header
        lines.add("Category,${monthNames.joinToString(",")},Total")

        // Income
        lines.add("INCOME,,,,,,,,,,,,,")
        categoryGroups.filter { it.isIncome }.forEach { group ->
            val monthValues = (1..12).map { month -> (incomeData[group.code]?.get(month) ?: 0.0).toInt() }
            val total = monthValues.sum()
            lines.add("${group.displayName},${monthValues.joinToString(",")},${total}")
            group.subcategories.forEach { sub ->
                val subMonthValues = (1..12).map { month -> (incomeData[sub.code]?.get(month) ?: 0.0).toInt() }
                val subTotal = subMonthValues.sum()
                lines.add("  ${sub.displayName},${subMonthValues.joinToString(",")},${subTotal}")
            }
        }

        // Expenses
        lines.add("EXPENSES,,,,,,,,,,,,,")
        categoryGroups.filter { !it.isIncome }.forEach { group ->
            val monthValues = (1..12).map { month -> (expenseData[group.code]?.get(month) ?: 0.0).toInt() }
            val total = monthValues.sum()
            lines.add("${group.displayName},${monthValues.joinToString(",")},${total}")
            group.subcategories.forEach { sub ->
                val subMonthValues = (1..12).map { month -> (expenseData[sub.code]?.get(month) ?: 0.0).toInt() }
                val subTotal = subMonthValues.sum()
                lines.add("  ${sub.displayName},${subMonthValues.joinToString(",")},${subTotal}")
            }
        }

        val csv = lines.joinToString("\n")

        // Create download
        val blob = org.w3c.files.Blob(arrayOf(csv), org.w3c.files.BlobPropertyBag("text/csv"))
        val url = org.w3c.dom.url.URL.createObjectURL(blob)
        val a = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
        a.href = url
        a.download = "budget_${selectedYear}.csv"
        a.click()
        org.w3c.dom.url.URL.revokeObjectURL(url)
    }

    private fun saveToServer(category: String, type: String, month: Int, amount: Double) {
        // Read-only gate: stops the doomed-on-server save before the API call.
        // Surfaces the bottom toast prompting the user to subscribe.
        if (gateReadOnly()) return

        val sharedToken = ApiService.getSharedAuthToken()
        val sharedUserId = ApiService.getSharedUserId()

        if (sharedToken == null || sharedUserId == null) {
            console.log("Cannot save budget - not authenticated")
            return
        }

        // Ensure session is restored to this instance
        if (!apiService.isAuthenticated()) {
            apiService.restoreSession(sharedToken, sharedUserId)
        }

        GlobalScope.launch {
            try {
                apiService.updateBudgetPlan(
                    year = selectedYear,
                    month = month,
                    categoryType = type,
                    categoryName = category,
                    plannedAmount = amount
                )
                console.log("Saved budget: $category ($type) month $month = $amount")
            } catch (e: Exception) {
                console.error("Failed to save budget plan:", e.message)
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                }
            }
        }
    }

    /**
     * Primary↔sub source-of-truth state.
     *
     * - PRIMARY: the primary row has at least one non-zero monthly value AND
     *   no sub-category has a non-zero value. Primary is editable normally.
     * - SUBS: at least one sub-category has a non-zero value. The primary
     *   row displays the sum, rendered italic + greyed; typing into it
     *   triggers the "replace sub-amounts?" modal.
     * - EMPTY: nothing has been budgeted on this primary yet. Either side
     *   can be filled in without a confirmation.
     */
    private enum class SourceMode { PRIMARY, SUBS, EMPTY }

    private fun primaryHasOwnAmount(primaryCode: String, type: String): Boolean {
        val data = if (type == "income") incomeData else expenseData
        return (data[primaryCode] ?: return false).values.any { it > 0.0 }
    }

    private fun anySubHasAmount(group: BudgetCategoryGroup, type: String): Boolean {
        val data = if (type == "income") incomeData else expenseData
        return group.subcategories.any { sub ->
            (data[sub.code] ?: emptyMap()).values.any { it > 0.0 }
        }
    }

    private fun sourceModeFor(group: BudgetCategoryGroup, type: String): SourceMode {
        val hasSubs = anySubHasAmount(group, type)
        if (hasSubs) return SourceMode.SUBS
        if (primaryHasOwnAmount(group.code, type)) return SourceMode.PRIMARY
        return SourceMode.EMPTY
    }

    /** Walk up from a category code to its parent group, if it's a sub. */
    private fun parentGroupFor(code: String): BudgetCategoryGroup? =
        categoryGroups.firstOrNull { it.subcategories.any { sub -> sub.code == code } }

    /** True when the given code is the primary of a group (not a sub). */
    private fun isPrimaryCode(code: String): Boolean =
        categoryGroups.any { it.code == code }

    private fun loadFromServer() {
        // Check shared auth state directly (in case instance auth isn't synced)
        val sharedToken = ApiService.getSharedAuthToken()
        val sharedUserId = ApiService.getSharedUserId()

        if (sharedToken == null || sharedUserId == null) {
            console.log("Cannot load budget - not authenticated")
            return
        }

        // Restore session to this ApiService instance if needed
        if (!apiService.isAuthenticated()) {
            apiService.restoreSession(sharedToken, sharedUserId)
        }

        isLoading = true
        GlobalScope.launch {
            try {
                // Fetch custom categories and build the category hierarchy
                customCategories = try { apiService.getCustomCategories() } catch (_: Exception) { emptyList() }
                val budgetCustomCats = customCategories.map { cc ->
                    BudgetCustomCategory(
                        primaryName = cc.primaryName,
                        secondaryName = cc.secondaryName,
                        isIncome = cc.isIncome
                    )
                }
                categoryGroups = BudgetCategoryProvider.getCategories(budgetCustomCats)

                // Initialize income/expense data maps from category groups
                incomeData.clear()
                expenseData.clear()
                categoryGroups.forEach { group ->
                    val targetMap = if (group.isIncome) incomeData else expenseData
                    // Primary group code
                    if (!targetMap.containsKey(group.code)) {
                        targetMap[group.code] = mutableMapOf()
                    }
                    // Subcategory codes
                    group.subcategories.forEach { sub ->
                        if (!targetMap.containsKey(sub.code)) {
                            targetMap[sub.code] = mutableMapOf()
                        }
                    }
                }

                val plans = apiService.getBudgetPlansByYear(selectedYear)
                console.log("Loaded ${plans.size} budget plans from server")

                // Populate data from server response
                plans.forEach { plan ->
                    val targetMap = if (plan.categoryType == "income") incomeData else expenseData
                    if (!targetMap.containsKey(plan.categoryName)) {
                        targetMap[plan.categoryName] = mutableMapOf()
                    }
                    targetMap[plan.categoryName]!![plan.month] = plan.plannedAmount
                }

                isLoading = false
                // Refresh the UI with loaded data
                refreshSpreadsheet()
            } catch (e: Exception) {
                console.error("Failed to load budget plans:", e.message)
                isLoading = false
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                }
            }
        }
    }

    private fun clearAndReloadData() {
        // Clear existing data - categories will be re-initialized in loadFromServer()
        incomeData.clear()
        expenseData.clear()

        // Load data for the current selectedYear from server API
        // This also re-fetches custom categories and rebuilds the hierarchy
        loadFromServer()
    }

}
